import { deleteHttp, sendWithFile } from "../../common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { useDotLegalFileUploadDataMapping } from "../../common/components/dotLegalFileUploadTable/DotLegalFileUploadTable.hooks";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { IDocumentVersionTable } from "./DocumentVersionTable";
import { createElement, useState } from "react";
import { useValidator } from "../../common/hooks/validation";
import { DocumentVersionViewModel, DocumentBaseSaveModel } from "./DocumentVersionTable.types";
import { DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { Trans } from "react-i18next";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useDocumentVersionTableHook(props: IDocumentVersionTable) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();
    const { downloadDocumentVersion } = useDotLegalFileUploadDataMapping();

    const [documentVersionForDeletion, setDocumentVersionForDeletion] = useState("");
    const [documentationOption, setDocumentationOption] = useState(1);
    const [document, setDocument] = useState<DocumentBaseSaveModel>(new DocumentBaseSaveModel());
    const { uploadPercentage, trackUploadProgress } = useUploadProgress();

    const url = `/document/${props.documentId}`;
    const { data, isLoading, refetch } = useDotLegalQuery<Array<DocumentVersionViewModel>>(url);

    const linkValidation = useValidator<DocumentBaseSaveModel>(
        (validators) => ({
            link: documentationOption === 1 ? undefined : validators.validateUrlAndNotEmpty((item) => item.link, translateString("link")),
        }),
        [documentationOption]
    );

    const handleDownloadClick = (documentId: string, dataId: string, fileName: string, link: string) => {
        if (!isNullOrWhitespace(fileName)) {
            downloadDocumentVersion(documentId, dataId, fileName, props.entityType, props.entityId);
        } else {
            window.open(link, "_blank");
        }
    };

    const removeMutation = useDotLegalMutation(getRemoveDocumentVersion);

    const deleteDocumentVersion = (documentId: string, documentVersionId: string) => {
        removeMutation.mutateAsync(
            { documentId: documentId, documentVersionId: documentVersionId },
            {
                onSuccess: (response: any) => {
                    refetch();
                    props.onVersionsChanged?.();
                    setDocumentVersionForDeletion("");
                },
            }
        );
    };

    async function getRemoveDocumentVersion(doc: { documentId: string; documentVersionId: string }) {
        return deleteHttp(`/document/${doc.documentId}/${doc.documentVersionId}`);
    }

    const uploadDocumentVersionMutation = useDotLegalMutation(
        (upload: { document: DocumentBaseSaveModel; file: File | undefined }) =>
            sendWithFile("POST", `/document/${props.documentId}`, upload.document, upload.file, trackUploadProgress()),
        {
            onSuccess: () => props.onVersionsChanged?.(),
        }
    );

    const uploadDocumentVersion = async (files: Array<DotLegalFile> | undefined) => {
        props.setDocumentsUploading(true);
        try {
            if (files) {
                await uploadDocumentVersionMutation.mutateAsync({
                    document: { fileName: files[0].fileName, link: undefined },
                    file: files[0].file,
                });

                await refetch();

                snackbar.show(createElement(Trans, { i18nKey: "documentVersionUploadet", values: { document: files[0].fileName } }));
            }
        } finally {
            props.setDocumentsUploading(false);
        }
    };

    async function uploadLink() {
        if (linkValidation.anyHasErrors) {
            linkValidation.setShowErrors(true);
            return;
        }

        props.setDocumentsUploading(true);
        try {
            await uploadDocumentVersionMutation.mutateAsync({
                document: { fileName: undefined, link: document?.link },
                file: undefined,
            });
            await refetch();
        } finally {
            props.setDocumentsUploading(false);
        }

        snackbar.show(createElement(Trans, { i18nKey: "documentVersionUploadet", values: { document: document?.link } }));
        setDocument(new DocumentBaseSaveModel());

        linkValidation.setShowErrors(false);
    }

    return {
        handleDownloadClick,
        deleteDocumentVersion,
        linkValidation,
        uploadDocumentVersion,
        document,
        setDocument,
        uploadLink,
        setDocumentationOption,
        documentationOption,
        setDocumentVersionForDeletion,
        documentVersionForDeletion,
        isLoading,
        data,
        isUploading: uploadDocumentVersionMutation.isLoading,
        uploadPercentage,
    };
}
